import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Button, type ButtonProps } from "./button";
import { PlusCircle, Trash2 } from "lucide-react";
import { Separator } from "./separator";
import { Badge } from "./badge";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./command";
import { Checkbox } from "./checkbox";
import { cn } from "./lib/utils";

export type DataFilterOption = { label: string; value: string };

interface DataFilterProps {
  title?: string;
  value: string[];
  onValueChange: (value: string[]) => void;
  options: DataFilterOption[];
  withInput?: boolean;
  showClearButton?: boolean;
  disabled?: boolean;
  variant?: "default" | "alternate";
  size?: ButtonProps["size"];
}

export function DataFilter({
  title,
  value,
  onValueChange,
  options,
  withInput = true,
  showClearButton = true,
  disabled = false,
  variant = "default",
  size = "sm",
}: DataFilterProps) {
  const selected = new Set(value);

  const handleClearFilters = (e: React.MouseEvent) => {
    if (disabled) return;
    e.stopPropagation();
    onValueChange([]);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className={cn(
            "border-dashed",
            variant === "alternate" && "bg-background"
          )}
          disabled={disabled}
        >
          <PlusCircle className="h-4 w-4 mr-1" /> {title}
          {selected.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="flex items-center gap-1">
                <Badge
                  variant="default"
                  className="rounded-sm px-1 font-normal lg:hidden"
                >
                  {selected.size}
                </Badge>
                <div className="hidden items-center space-x-1 lg:flex">
                  {selected.size > 1 ? (
                    <Badge
                      variant="purple"
                      className="text-xs rounded-sm px-1 font-normal"
                    >
                      {selected.size} selected
                    </Badge>
                  ) : (
                    options
                      .filter((option) => selected.has(option.value))
                      .map((option) => (
                        <Badge
                          variant="purple"
                          key={option.value}
                          className="text-xs rounded-sm px-1 font-normal"
                        >
                          {option.label}
                        </Badge>
                      ))
                  )}
                </div>
                {showClearButton && (
                  <span
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onClick={handleClearFilters}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        handleClearFilters(e as any);
                    }}
                    className="ml-1 p-1 hover:bg-muted rounded-sm"
                    title="Clear filters"
                    aria-disabled={disabled}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                  </span>
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          {withInput && (
            <CommandInput
              placeholder={title}
              disabled={disabled}
              className="h-8"
            />
          )}
          <CommandList className="max-h-[300px] overflow-y-auto">
            <CommandEmpty>No results found.</CommandEmpty>
            {selected.size > 0 && showClearButton && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => !disabled && onValueChange([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (disabled) return;
                      const newSelected = new Set(selected);
                      if (isSelected) {
                        newSelected.delete(option.value);
                      } else {
                        newSelected.add(option.value);
                      }
                      onValueChange(Array.from(newSelected));
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      className="mr-2"
                      disabled={disabled}
                    />
                    <span className="text-xs">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
